import React, { useEffect, useMemo, useState } from "react";
import { Image, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import "../../styles/admin/CarManager.css";
import "../../styles/admin/CarAdd.css";

import { useDropzone } from "react-dropzone";
import api from "../../api/http";
import ImageCropModal from "./ImageCropModal";

// ✅ JWT helper
import { isAdminFromAccess } from "../../api/auth";

const PRODUCTS_PER_PAGE = 12;

const CarsManager = () => {
  const [products, setProducts] = useState([]);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAddCarModal, setShowAddCarModal] = useState(false);
  const [showEditCarModal, setShowEditCarModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [produsData, setProdusData] = useState({
    name: "",
    producator: "",
    uploaded_images: [],
    cutia: 2,
    motor: 6,
    numar_usi: 2,
    numar_pasageri: 4,
    km_limit: "fără limită",
    descriere: "",
    caroserie: 13,
    price1: 0,
    price2: 0,
    price3: 0,
    price4: 0,
    price5: 0,
    gaj: 50,
    an: new Date().getFullYear(),
    capacitate_cilindrica: 0.8,
  });

  // previews pentru create/edit
  const [createPreviews, setCreatePreviews] = useState([]); // [{file, url}]
  const [updatePreviews, setUpdatePreviews] = useState([]); // [{file, url}]

  // crop queue la upload
  const [cropQueue, setCropQueue] = useState([]);
  const [cropContext, setCropContext] = useState(null); // "create" | "update"
  const [cropBatchTotal, setCropBatchTotal] = useState(0);

  // =========================
  // ✅ Auth watcher (ca să recalculeze admin la login/logout)
  // =========================
  const [authTick, setAuthTick] = useState(0);

  useEffect(() => {
    const onStorage = () => setAuthTick((x) => x + 1);
    const onFocus = () => setAuthTick((x) => x + 1);

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const userIsSuperUser = useMemo(() => {
    void authTick;
    return isAdminFromAccess();
  }, [authTick]);

  const parseApiError = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "A apărut o eroare.";

    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (data.detail) return data.detail;

    const k = Object.keys(data)[0];
    const v = data[k];
    if (Array.isArray(v)) return v[0];
    if (typeof v === "string") return v;

    return "A apărut o eroare.";
  };

  const toInt = (v, fallback = 0) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };
  const toFloat = (v, fallback = 0) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };

  // ✅ price inputs: DOAR cifre (fără minus, fără punct)
  const onlyDigits = (v) => String(v ?? "").replace(/\D+/g, "");

  const setCreateDigitsField = (field) => (e) => {
    const digits = onlyDigits(e.target.value);
    setProdusData((p) => ({ ...p, [field]: digits === "" ? 0 : Number(digits) }));
  };

  const setUpdateDigitsField = (field) => (e) => {
    const digits = onlyDigits(e.target.value);
    setSelectedProduct((p) => ({ ...p, [field]: digits === "" ? 0 : Number(digits) }));
  };

  const numericInputProps = {
    type: "text",
    inputMode: "numeric",
    pattern: "[0-9]*",
    autoComplete: "off",
    onWheel: (e) => e.currentTarget.blur(),
  };

  // ✅ FIX: oprește click-ul să urce în dropzone (altfel îți deschide file-picker)
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // ✅ CHOICES
  const CAPACITATE_CHOICES = useMemo(() => {
    const out = [];
    for (let i = 8; i <= 40; i++) out.push([i / 10, (i / 10).toString()]);
    return out;
  }, []);

  const AN_CHOICES = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2000;
    return Array.from({ length: currentYear - startYear + 1 }, (_, idx) => {
      const year = startYear + idx;
      return [year, year.toString()];
    });
  }, []);

  // =========================
  // ✅ Pagination helpers
  // =========================
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / PRODUCTS_PER_PAGE)),
    [count]
  );

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages]
  );

  // =========================
  // ✅ list products (DRF pagination)
  // =========================
  const fetchCar = async (page = currentPage) => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/produs/car", {
        params: { page, page_size: PRODUCTS_PER_PAGE },
      });

      const data = res.data;
      const results = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      setProducts(results);
      setCount(Number(data?.count ?? results.length));
    } catch (e) {
      setError(parseApiError(e) || "Error fetching products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCar(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const clearCropQueue = () => {
    setCropQueue([]);
    setCropContext(null);
    setCropBatchTotal(0);
  };

  const startCropQueue = (files, context) => {
    if (!files?.length) return;
    setCropContext(context);
    setCropQueue(files);
    setCropBatchTotal(files.length);
  };

  const appendCroppedFile = (file) => {
    if (cropContext === "create") {
      setProdusData((prev) => ({
        ...prev,
        uploaded_images: [...(prev.uploaded_images || []), file],
      }));
    } else if (cropContext === "update") {
      setSelectedProduct((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          uploaded_images: [...(prev.uploaded_images || []), file],
        };
      });
    }
  };

  const advanceCropQueue = () => {
    setCropQueue((prev) => {
      const next = prev.slice(1);
      if (next.length === 0) {
        setCropContext(null);
        setCropBatchTotal(0);
      }
      return next;
    });
  };

  const handleCropConfirm = (croppedFile) => {
    appendCroppedFile(croppedFile);
    advanceCropQueue();
  };

  const handleCropUseOriginal = () => {
    if (currentCropFile) appendCroppedFile(currentCropFile);
    advanceCropQueue();
  };

  const handleCropUseAllOriginals = () => {
    cropQueue.forEach((file) => appendCroppedFile(file));
    clearCropQueue();
  };

  const isCropping = cropQueue.length > 0;
  const currentCropFile = cropQueue[0] ?? null;
  const cropQueueIndex = cropBatchTotal - cropQueue.length + 1;

  // =========================
  // Dropzone CREATE
  // =========================
  const onDropForCreate = (acceptedFiles, rejections) => {
    setError("");

    if (rejections?.length) {
      const msg =
        rejections?.[0]?.errors?.[0]?.message ||
        "Unele fișiere au fost respinse (format).";
      setError(msg);
    }

    if (!acceptedFiles?.length) return;

    startCropQueue(acceptedFiles, "create");
  };

  const {
    getRootProps: getRootPropsForCreate,
    getInputProps: getInputPropsForCreate,
    isDragActive: isDragActiveCreate,
  } = useDropzone({
    onDrop: onDropForCreate,
    accept: { "image/*": [] },
    multiple: true,
    disabled: isCreating || isUpdating || isCropping,
  });

  useEffect(() => {
    createPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    const next = (produsData.uploaded_images || []).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setCreatePreviews(next);

    return () => next.forEach((p) => URL.revokeObjectURL(p.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produsData.uploaded_images]);

  const removeCreateImageAt = (idx) => {
    setProdusData((p) => ({
      ...p,
      uploaded_images: (p.uploaded_images || []).filter((_, i) => i !== idx),
    }));
  };

  // =========================
  // Dropzone UPDATE
  // =========================
  const onDropForUpdate = (acceptedFiles, rejections) => {
    setError("");

    if (rejections?.length) {
      const msg =
        rejections?.[0]?.errors?.[0]?.message ||
        "Unele fișiere au fost respinse (format).";
      setError(msg);
    }
    if (!acceptedFiles?.length) return;

    startCropQueue(acceptedFiles, "update");
  };

  const {
    getRootProps: getRootPropsForUpdate,
    getInputProps: getInputPropsForUpdate,
    isDragActive: isDragActiveUpdate,
  } = useDropzone({
    onDrop: onDropForUpdate,
    accept: { "image/*": [] },
    multiple: true,
    disabled: isCreating || isUpdating || isCropping,
  });

  useEffect(() => {
    updatePreviews.forEach((p) => URL.revokeObjectURL(p.url));
    const next = (selectedProduct?.uploaded_images || []).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setUpdatePreviews(next);

    return () => next.forEach((p) => URL.revokeObjectURL(p.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.uploaded_images]);

  const removeUpdateImageAt = (idx) => {
    setSelectedProduct((p) => {
      if (!p) return p;
      return {
        ...p,
        uploaded_images: (p.uploaded_images || []).filter((_, i) => i !== idx),
      };
    });
  };

  // =========================
  // CREATE handlers
  // =========================
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name === "an") {
      setProdusData((p) => ({ ...p, an: toInt(value, p.an) }));
      return;
    }
    if (name === "capacitate_cilindrica") {
      setProdusData((p) => ({
        ...p,
        capacitate_cilindrica: toFloat(value, p.capacitate_cilindrica),
      }));
      return;
    }

    setProdusData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCutieChange = (e) =>
    setProdusData((p) => ({ ...p, cutia: toInt(e.target.value, 2) }));
  const handleNumar_pasageriChange = (e) =>
    setProdusData((p) => ({ ...p, numar_pasageri: toInt(e.target.value, 4) }));
  const handleNumar_usiChange = (e) =>
    setProdusData((p) => ({ ...p, numar_usi: toInt(e.target.value, 2) }));
  const handleCaroserieChange = (e) =>
    setProdusData((p) => ({ ...p, caroserie: toInt(e.target.value, 13) }));
  const handleMotorChange = (e) =>
    setProdusData((p) => ({ ...p, motor: toInt(e.target.value, 6) }));

  // ✅ Price/Gaj CREATE: doar cifre + fără scroll/spinner
  const handlePrice1Change = setCreateDigitsField("price1");
  const handlePrice2Change = setCreateDigitsField("price2");
  const handlePrice3Change = setCreateDigitsField("price3");
  const handlePrice4Change = setCreateDigitsField("price4");
  const handlePrice5Change = setCreateDigitsField("price5");
  const handleGajChange = setCreateDigitsField("gaj");

  const handleAddCarClick = () => {
    setError("");
    setShowAddCarModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeAddModal = () => {
    if (isCreating) return;
    clearCropQueue();
    setShowAddCarModal(false);
    document.body.style.overflow = "";
  };

  const closeEditModal = () => {
    if (isUpdating) return;
    clearCropQueue();
    setShowEditCarModal(false);
    setSelectedProduct(null);
    document.body.style.overflow = "";
  };

  // close on ESC (crop modal first, then parent modals)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (cropQueue.length > 0) {
        clearCropQueue();
        return;
      }
      if (showAddCarModal) closeAddModal();
      if (showEditCarModal) closeEditModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddCarModal, showEditCarModal, isCreating, isUpdating, cropQueue.length]);

  // ✅ CREATE
  const AddCar = async (event) => {
    event.preventDefault();
    setError("");

    try {
      if (!userIsSuperUser) {
        setError("Permisiune respinsă: nu ești superuser.");
        return;
      }

      const name = (produsData.name || "").trim();
      const producator = (produsData.producator || "").trim();

      if (!name) {
        setError("Introduce numele produsului.");
        return;
      }
      if (!producator) {
        setError("Introduce producătorul.");
        return;
      }

      setIsCreating(true);

      const formData = new FormData();
      formData.append("name", name);
      formData.append("producator", producator);

      formData.append("cutia", String(produsData.cutia));
      formData.append("motor", String(produsData.motor));
      formData.append("numar_usi", String(produsData.numar_usi));
      formData.append("numar_pasageri", String(produsData.numar_pasageri));
      formData.append("km_limit", String(produsData.km_limit));
      formData.append("caroserie", String(produsData.caroserie));
      formData.append("descriere", String(produsData.descriere || ""));

      formData.append("price1", String(toInt(produsData.price1, 0)));
      formData.append("price2", String(toInt(produsData.price2, 0)));
      formData.append("price3", String(toInt(produsData.price3, 0)));
      formData.append("price4", String(toInt(produsData.price4, 0)));
      formData.append("price5", String(toInt(produsData.price5, 0)));
      formData.append("gaj", String(toInt(produsData.gaj, 0)));

      formData.append("an", String(produsData.an));
      formData.append(
        "capacitate_cilindrica",
        String(produsData.capacitate_cilindrica)
      );

      (produsData.uploaded_images || []).forEach((file) => {
        formData.append("uploaded_images", file);
      });

      await api.post("/produs/car-create", formData);

      closeAddModal();

      setProdusData({
        name: "",
        producator: "",
        uploaded_images: [],
        cutia: 2,
        motor: 6,
        numar_usi: 2,
        numar_pasageri: 4,
        km_limit: "fără limită",
        descriere: "",
        caroserie: 13,
        price1: 0,
        price2: 0,
        price3: 0,
        price4: 0,
        price5: 0,
        gaj: 50,
        an: new Date().getFullYear(),
        capacitate_cilindrica: 0.8,
      });

      // după create, mergi pe pagina 1 ca să vezi noul item
      setCurrentPage(1);
      await fetchCar(1);
    } catch (e) {
      setError(parseApiError(e));
    } finally {
      setIsCreating(false);
    }
  };

  // ✅ DELETE
  const handleDeleteProduct = async (productId, event) => {
    event.stopPropagation();
    setError("");

    try {
      if (!userIsSuperUser) {
        setError("Permisiune respinsă: nu ești superuser.");
        return;
      }

      const ok = window.confirm("Sigur vrei să ștergi acest produs?");
      if (!ok) return;

      await api.delete(`/produs/d_car/${productId}`);

      // dacă ai șters ultima de pe pagină, mergi înapoi o pagină
      const willBeEmpty = products.length === 1 && currentPage > 1;
      const nextPage = willBeEmpty ? currentPage - 1 : currentPage;

      setCurrentPage(nextPage);
      await fetchCar(nextPage);
    } catch (e) {
      setError(parseApiError(e));
    }
  };

  // ✅ OPEN EDIT
  const handleProductClick = (product) => {
    setError("");
    setSelectedProduct({
      ...product,
      uploaded_images: [],
      images_to_delete: [],
    });
    setShowEditCarModal(true);
    document.body.style.overflow = "hidden";
  };

  // =========================
  // EDIT handlers
  // =========================
  const handleInputUpdate = (event) => {
    const { name, value } = event.target;

    if (name === "an") {
      setSelectedProduct((p) => ({
        ...p,
        an: toInt(value, p?.an ?? new Date().getFullYear()),
      }));
      return;
    }
    if (name === "capacitate_cilindrica") {
      setSelectedProduct((p) => ({
        ...p,
        capacitate_cilindrica: toFloat(
          value,
          p?.capacitate_cilindrica ?? 0.8
        ),
      }));
      return;
    }

    setSelectedProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleCutieUpdate = (e) =>
    setSelectedProduct((p) => ({
      ...p,
      cutia: toInt(e.target.value, p?.cutia ?? 2),
    }));
  const handleNumar_pasageriUpdate = (e) =>
    setSelectedProduct((p) => ({
      ...p,
      numar_pasageri: toInt(e.target.value, p?.numar_pasageri ?? 4),
    }));
  const handleNumar_usiUpdate = (e) =>
    setSelectedProduct((p) => ({
      ...p,
      numar_usi: toInt(e.target.value, p?.numar_usi ?? 2),
    }));
  const handleCaroserieUpdate = (e) =>
    setSelectedProduct((p) => ({
      ...p,
      caroserie: toInt(e.target.value, p?.caroserie ?? 13),
    }));
  const handleMotorUpdate = (e) =>
    setSelectedProduct((p) => ({
      ...p,
      motor: toInt(e.target.value, p?.motor ?? 6),
    }));

  // ✅ Price/Gaj UPDATE: doar cifre + fără scroll/spinner
  const handlePrice1Update = setUpdateDigitsField("price1");
  const handlePrice2Update = setUpdateDigitsField("price2");
  const handlePrice3Update = setUpdateDigitsField("price3");
  const handlePrice4Update = setUpdateDigitsField("price4");
  const handlePrice5Update = setUpdateDigitsField("price5");
  const handleGajUpdate = setUpdateDigitsField("gaj");

  const handleImageDelete = (event, imageIndex) => {
    event.preventDefault();
    setSelectedProduct((prev) => {
      if (!prev) return prev;
      const img = prev.images?.[imageIndex];
      if (!img) return prev;

      const updatedImages = prev.images.filter((_, idx) => idx !== imageIndex);
      const imagesToDelete = [...(prev.images_to_delete || []), img.id];

      return {
        ...prev,
        images: updatedImages,
        images_to_delete: imagesToDelete,
      };
    });
  };

  const handleUpdateProduct = async (event) => {
    event.preventDefault();
    setError("");

    try {
      if (!userIsSuperUser) {
        setError("Permisiune respinsă: nu ești superuser.");
        return;
      }
      if (!selectedProduct?.id) return;

      if (!selectedProduct.name?.trim()) {
        setError("Introduce numele produsului.");
        return;
      }
      if (!selectedProduct.producator?.trim()) {
        setError("Introduce producătorul.");
        return;
      }

      setIsUpdating(true);

      const formData = new FormData();

      formData.append("name", selectedProduct.name.trim());
      formData.append("producator", selectedProduct.producator.trim());

      formData.append("cutia", String(toInt(selectedProduct.cutia, 2)));
      formData.append("motor", String(toInt(selectedProduct.motor, 6)));
      formData.append(
        "numar_usi",
        String(toInt(selectedProduct.numar_usi, 2))
      );
      formData.append(
        "numar_pasageri",
        String(toInt(selectedProduct.numar_pasageri, 4))
      );
      formData.append("km_limit", String(selectedProduct.km_limit ?? ""));
      formData.append("caroserie", String(toInt(selectedProduct.caroserie, 13)));
      formData.append("descriere", String(selectedProduct.descriere ?? ""));

      formData.append("price1", String(toInt(selectedProduct.price1, 0)));
      formData.append("price2", String(toInt(selectedProduct.price2, 0)));
      formData.append("price3", String(toInt(selectedProduct.price3, 0)));
      formData.append("price4", String(toInt(selectedProduct.price4, 0)));
      formData.append("price5", String(toInt(selectedProduct.price5, 0)));
      formData.append("gaj", String(toInt(selectedProduct.gaj, 50)));

      formData.append(
        "an",
        String(toInt(selectedProduct.an, new Date().getFullYear()))
      );
      formData.append(
        "capacitate_cilindrica",
        String(toFloat(selectedProduct.capacitate_cilindrica, 0.8))
      );

      (selectedProduct.uploaded_images || []).forEach((file) => {
        formData.append("uploaded_images", file);
      });

      (selectedProduct.images_to_delete || []).forEach((id) => {
        formData.append("images_to_delete", String(id));
      });

      await api.put(`/produs/car/${selectedProduct.id}`, formData);

      await fetchCar(currentPage);
      closeEditModal();
    } catch (e) {
      setError(parseApiError(e));
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <div className="srCar_wrap">
      <div className="srCar_grid">
        {userIsSuperUser && (
          <div className="srCar_addCard" onClick={handleAddCarClick}>
            <div className="srCar_addMedia">
              <div className="srCar_addIcon">
                <FontAwesomeIcon icon={faPlus} size="1x" />
              </div>
            </div>
            <div className="srCar_addLabel">Add Car</div>
          </div>
        )}

        {products.map((product) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => handleProductClick(product)}
            className="srCar_card"
            key={product.id}
          >
            <div className="srCar_cardMedia">
              {product.images?.length > 0 && (
                <Image className="srCar_thumb" src={product.images[0].image} fluid />
              )}
              {userIsSuperUser && (
                <div
                  className="srCar_deleteBtn"
                  onClick={(event) => handleDeleteProduct(product.id, event)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              )}
            </div>
            <div className="srCar_title">
              {product.producator} {product.name}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination (reuse classes from ProductList) */}
      {totalPages > 1 && (
        <nav className="srCars_pager" aria-label="Pagination">
          {currentPage > 1 && (
            <button
              type="button"
              className="srCars_pagerBtn"
              onClick={() => setCurrentPage((p) => p - 1)}
              aria-label="Previous page"
            >
              ‹
            </button>
          )}

          <ul className="srCars_pages">
            {pageNumbers.map((n) => (
              <li
                key={n}
                className={`srCars_pageNum ${currentPage === n ? "is-active" : ""}`}
                onClick={() => setCurrentPage(n)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" || e.key === " " ? setCurrentPage(n) : null
                }
                aria-label={`Go to page ${n}`}
              >
                {n}
              </li>
            ))}
          </ul>

          {currentPage < totalPages && (
            <button
              type="button"
              className="srCars_pagerBtn"
              onClick={() => setCurrentPage((p) => p + 1)}
              aria-label="Next page"
            >
              ›
            </button>
          )}
        </nav>
      )}

      {/* =========================
          ✅ ADD MODAL (custom overlay)
         ========================= */}
      {showAddCarModal && (
        <div className="srCarModal_overlay" onMouseDown={closeAddModal}>
          {error && <div className="srCar_error">Error: {error}</div>}
          <div
            className="srCarModal_content"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="srCarModal_header">
              <h3 className="srCarModal_heading">Add Car</h3>

              <button
                type="button"
                className="srCarModal_closeBtn"
                onClick={closeAddModal}
                disabled={isCreating}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="srCarModal_body">
              <form className="srCarF_form" onSubmit={AddCar}>
                <input
                  type="text"
                  name="name"
                  placeholder="Nume produs"
                  value={produsData.name}
                  onChange={handleInputChange}
                  disabled={isCreating}
                />
                <input
                  type="text"
                  name="producator"
                  placeholder="Producător"
                  value={produsData.producator}
                  onChange={handleInputChange}
                  disabled={isCreating}
                />

                <label>
                  Cutie:
                  <select
                    value={produsData.cutia}
                    onChange={handleCutieChange}
                    disabled={isCreating}
                  >
                    <option value={0}>Manual</option>
                    <option value={1}>Automat</option>
                    <option value={2}>Not specified</option>
                  </select>
                </label>

                <label>
                  Motor:
                  <select
                    value={produsData.motor}
                    onChange={handleMotorChange}
                    disabled={isCreating}
                  >
                    <option value={0}>Diesel</option>
                    <option value={1}>Hybrid</option>
                    <option value={2}>Petrol</option>
                    <option value={3}>Electric</option>
                    <option value={4}>Petrol-Hybrid</option>
                    <option value={5}>Petrol-Gaz</option>
                    <option value={6}>Not specified</option>
                  </select>
                </label>

                <label>
                  Număr uși:
                  <select
                    name="numar_usi"
                    value={produsData.numar_usi}
                    onChange={handleNumar_usiChange}
                    disabled={isCreating}
                  >
                    <option value={0}>3</option>
                    <option value={1}>5</option>
                    <option value={2}>Not specified</option>
                  </select>
                </label>

                <label>
                  Număr pasageri:
                  <select
                    name="numar_pasageri"
                    value={produsData.numar_pasageri}
                    onChange={handleNumar_pasageriChange}
                    disabled={isCreating}
                  >
                    <option value={0}>2</option>
                    <option value={1}>4</option>
                    <option value={2}>5</option>
                    <option value={3}>7</option>
                    <option value={4}>Not specified</option>
                  </select>
                </label>

                <label>
                  Limita de KM:
                  <textarea
                    name="km_limit"
                    value={produsData.km_limit}
                    onChange={handleInputChange}
                    disabled={isCreating}
                  />
                </label>

                <label>
                  An:
                  <select
                    name="an"
                    value={produsData.an}
                    onChange={handleInputChange}
                    disabled={isCreating}
                  >
                    {AN_CHOICES.map((option) => (
                      <option key={option[0]} value={option[0]}>
                        {option[1]}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Capacitate cilindrică:
                  <select
                    name="capacitate_cilindrica"
                    value={produsData.capacitate_cilindrica}
                    onChange={handleInputChange}
                    disabled={isCreating}
                  >
                    {CAPACITATE_CHOICES.map((option) => (
                      <option key={option[0]} value={option[0]}>
                        {option[1]}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Descriere:
                  <textarea
                    name="descriere"
                    value={produsData.descriere}
                    onChange={handleInputChange}
                    disabled={isCreating}
                  />
                </label>

                <label>
                  Caroserie:
                  <select
                    name="caroserie"
                    value={produsData.caroserie}
                    onChange={handleCaroserieChange}
                    disabled={isCreating}
                  >
                    <option value={0}>Van</option>
                    <option value={1}>Universal</option>
                    <option value={2}>Minivan</option>
                    <option value={3}>Roadster</option>
                    <option value={4}>SUV</option>
                    <option value={5}>Cabriolet</option>
                    <option value={6}>Microvan</option>
                    <option value={7}>Pickup</option>
                    <option value={8}>Sedan</option>
                    <option value={9}>Crossover</option>
                    <option value={10}>Hatchback</option>
                    <option value={11}>Combi</option>
                    <option value={12}>Coupe</option>
                    <option value={13}>Not specified</option>
                  </select>
                </label>

                {/* ✅ numeric-only price inputs */}
                <label>
                  1-2 Zile:
                  <input
                    {...numericInputProps}
                    value={String(produsData.price1 ?? 0)}
                    onChange={handlePrice1Change}
                    disabled={isCreating}
                  />
                </label>
                <label>
                  3-7 Zile:
                  <input
                    {...numericInputProps}
                    value={String(produsData.price2 ?? 0)}
                    onChange={handlePrice2Change}
                    disabled={isCreating}
                  />
                </label>
                <label>
                  8-20 Zile:
                  <input
                    {...numericInputProps}
                    value={String(produsData.price3 ?? 0)}
                    onChange={handlePrice3Change}
                    disabled={isCreating}
                  />
                </label>
                <label>
                  21-45 Zile:
                  <input
                    {...numericInputProps}
                    value={String(produsData.price4 ?? 0)}
                    onChange={handlePrice4Change}
                    disabled={isCreating}
                  />
                </label>
                <label>
                  46+ Zile:
                  <input
                    {...numericInputProps}
                    value={String(produsData.price5 ?? 0)}
                    onChange={handlePrice5Change}
                    disabled={isCreating}
                  />
                </label>
                <label>
                  Gaj:
                  <input
                    {...numericInputProps}
                    value={String(produsData.gaj ?? 0)}
                    onChange={handleGajChange}
                    disabled={isCreating}
                  />
                </label>

                <div
                  {...getRootPropsForCreate()}
                  className={`srCar_dropzone srCarF_full ${
                    isDragActiveCreate ? "is-drag" : ""
                  }`}
                  style={{ opacity: isUpdating ? 0.7 : 1 }}
                >
                  <input {...getInputPropsForCreate()} />
                  <p className="srCar_dropHint">
                    Drag & drop sau click — decupare liberă (opțional)
                  </p>

                  {createPreviews.length > 0 && (
                    <div className="srCar_previews">
                      {createPreviews.map((p, idx) => (
                        <div
                          key={idx}
                          className="srCar_previewItem"
                          onClick={stop}
                          onMouseDown={stop}
                          onPointerDown={stop}
                        >
                          <img
                            className="srCar_previewImg"
                            src={p.url}
                            alt={p.file.name}
                          />
                          <button
                            type="button"
                            className="srCarF_btn srCarF_btnDanger"
                            onMouseDown={stop}
                            onPointerDown={stop}
                            onClick={(e) => {
                              stop(e);
                              removeCreateImageAt(idx);
                            }}
                            disabled={isCreating}
                          >
                            Șterge
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="srCarF_actions">
                  <button
                    className="srCarF_btn srCarF_btnPrimary"
                    type="submit"
                    disabled={isCreating}
                  >
                    {isCreating ? "Se creează..." : "Creează Produs"}
                  </button>
                  <button
                    className="srCarF_btn srCarF_btnDanger"
                    type="button"
                    onClick={closeAddModal}
                    disabled={isCreating}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          ✅ EDIT MODAL (custom overlay)
         ========================= */}
      {showEditCarModal && (
        <div className="srCarModal_overlay" onMouseDown={closeEditModal}>
          <div
            className="srCarModal_content"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="srCarModal_header">
              <h3 className="srCarModal_heading">Edit Car</h3>

              <button
                type="button"
                className="srCarModal_closeBtn"
                onClick={closeEditModal}
                disabled={isUpdating}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="srCarModal_body">
              <form className="srCarF_form" onSubmit={handleUpdateProduct}>
                <input
                  type="text"
                  name="name"
                  placeholder="Nume produs"
                  value={selectedProduct ? selectedProduct.name : ""}
                  onChange={handleInputUpdate}
                  disabled={isUpdating}
                />
                <input
                  type="text"
                  name="producator"
                  placeholder="Producător"
                  value={selectedProduct ? selectedProduct.producator : ""}
                  onChange={handleInputUpdate}
                  disabled={isUpdating}
                />

                <label>
                  Cutie:
                  <select
                    value={selectedProduct ? selectedProduct.cutia : ""}
                    onChange={handleCutieUpdate}
                    disabled={isUpdating}
                  >
                    <option value={0}>Manual</option>
                    <option value={1}>Automat</option>
                    <option value={2}>Not specified</option>
                  </select>
                </label>

                <label>
                  Motor:
                  <select
                    value={selectedProduct ? selectedProduct.motor : ""}
                    onChange={handleMotorUpdate}
                    disabled={isUpdating}
                  >
                    <option value={0}>Diesel</option>
                    <option value={1}>Hybrid</option>
                    <option value={2}>Petrol</option>
                    <option value={3}>Electric</option>
                    <option value={4}>Petrol-Hybrid</option>
                    <option value={5}>Petrol-Gaz</option>
                    <option value={6}>Not specified</option>
                  </select>
                </label>

                <label>
                  Număr uși:
                  <select
                    name="numar_usi"
                    value={selectedProduct ? selectedProduct.numar_usi : ""}
                    onChange={handleNumar_usiUpdate}
                    disabled={isUpdating}
                  >
                    <option value={0}>3</option>
                    <option value={1}>5</option>
                    <option value={2}>Not specified</option>
                  </select>
                </label>

                <label>
                  Număr pasageri:
                  <select
                    name="numar_pasageri"
                    value={selectedProduct ? selectedProduct.numar_pasageri : ""}
                    onChange={handleNumar_pasageriUpdate}
                    disabled={isUpdating}
                  >
                    <option value={0}>2</option>
                    <option value={1}>4</option>
                    <option value={2}>5</option>
                    <option value={3}>7</option>
                    <option value={4}>Not specified</option>
                  </select>
                </label>

                <label>
                  Limita de KM:
                  <textarea
                    name="km_limit"
                    value={selectedProduct ? selectedProduct.km_limit : ""}
                    onChange={handleInputUpdate}
                    disabled={isUpdating}
                  />
                </label>

                <label>
                  An:
                  <select
                    name="an"
                    value={selectedProduct ? selectedProduct.an : ""}
                    onChange={handleInputUpdate}
                    disabled={isUpdating}
                  >
                    {AN_CHOICES.map((option) => (
                      <option key={option[0]} value={option[0]}>
                        {option[1]}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Capacitate cilindrică:
                  <select
                    name="capacitate_cilindrica"
                    value={selectedProduct ? selectedProduct.capacitate_cilindrica : ""}
                    onChange={handleInputUpdate}
                    disabled={isUpdating}
                  >
                    {CAPACITATE_CHOICES.map((option) => (
                      <option key={option[0]} value={option[0]}>
                        {option[1]}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Descriere:
                  <textarea
                    name="descriere"
                    value={selectedProduct ? selectedProduct.descriere : ""}
                    onChange={handleInputUpdate}
                    disabled={isUpdating}
                  />
                </label>

                <label>
                  Caroserie:
                  <select
                    name="caroserie"
                    value={selectedProduct ? selectedProduct.caroserie : ""}
                    onChange={handleCaroserieUpdate}
                    disabled={isUpdating}
                  >
                    <option value={0}>Van</option>
                    <option value={1}>Universal</option>
                    <option value={2}>Minivan</option>
                    <option value={3}>Roadster</option>
                    <option value={4}>SUV</option>
                    <option value={5}>Cabriolet</option>
                    <option value={6}>Microvan</option>
                    <option value={7}>Pickup</option>
                    <option value={8}>Sedan</option>
                    <option value={9}>Crossover</option>
                    <option value={10}>Hatchback</option>
                    <option value={11}>Combi</option>
                    <option value={12}>Coupe</option>
                    <option value={13}>Not specified</option>
                  </select>
                </label>

                {/* ✅ numeric-only price inputs */}
                <label>
                  1-2 Zile:
                  <input
                    {...numericInputProps}
                    value={String(selectedProduct?.price1 ?? 0)}
                    onChange={handlePrice1Update}
                    disabled={isUpdating}
                  />
                </label>
                <label>
                  3-7 Zile:
                  <input
                    {...numericInputProps}
                    value={String(selectedProduct?.price2 ?? 0)}
                    onChange={handlePrice2Update}
                    disabled={isUpdating}
                  />
                </label>
                <label>
                  8-20 Zile:
                  <input
                    {...numericInputProps}
                    value={String(selectedProduct?.price3 ?? 0)}
                    onChange={handlePrice3Update}
                    disabled={isUpdating}
                  />
                </label>
                <label>
                  21-45 Zile:
                  <input
                    {...numericInputProps}
                    value={String(selectedProduct?.price4 ?? 0)}
                    onChange={handlePrice4Update}
                    disabled={isUpdating}
                  />
                </label>
                <label>
                  46+ Zile:
                  <input
                    {...numericInputProps}
                    value={String(selectedProduct?.price5 ?? 0)}
                    onChange={handlePrice5Update}
                    disabled={isUpdating}
                  />
                </label>
                <label>
                  Gaj:
                  <input
                    {...numericInputProps}
                    value={String(selectedProduct?.gaj ?? 0)}
                    onChange={handleGajUpdate}
                    disabled={isUpdating}
                  />
                </label>

                <div
                  {...getRootPropsForUpdate()}
                  className={`srCar_dropzone srCarF_full ${
                    isDragActiveUpdate ? "is-drag" : ""
                  }`}
                  style={{ opacity: isUpdating ? 0.7 : 1 }}
                >
                  <input {...getInputPropsForUpdate()} />
                  <p className="srCar_dropHint">
                    Adaugă imagini — decupare liberă (opțional)
                  </p>

                  {updatePreviews.length > 0 && (
                    <div className="srCar_previews">
                      {updatePreviews.map((p, idx) => (
                        <div
                          key={idx}
                          className="srCar_previewItem"
                          onClick={stop}
                          onMouseDown={stop}
                          onPointerDown={stop}
                        >
                          <img
                            className="srCar_previewImg"
                            src={p.url}
                            alt={p.file.name}
                          />
                          <button
                            type="button"
                            className="srCarF_btn srCarF_btnDanger"
                            onMouseDown={stop}
                            onPointerDown={stop}
                            onClick={(e) => {
                              stop(e);
                              removeUpdateImageAt(idx);
                            }}
                            disabled={isUpdating}
                          >
                            Șterge
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="srCar_gallery srCarF_full">
                  {selectedProduct?.images?.map((image, index) => (
                    <div className="srCar_galleryItem" key={image.id || index}>
                      <img
                        className="srCar_galleryImg"
                        src={image.image}
                        alt={`Image ${index}`}
                      />
                      <button
                        className="srCarF_btn srCarF_btnDanger"
                        type="button"
                        onClick={(e) => handleImageDelete(e, index)}
                        disabled={isUpdating}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>

                <div className="srCarF_actions">
                  <button
                    className="srCarF_btn srCarF_btnPrimary"
                    type="submit"
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Se salvează..." : "Actualizează Produs"}
                  </button>
                  <button
                    className="srCarF_btn srCarF_btnDanger"
                    type="button"
                    onClick={closeEditModal}
                    disabled={isUpdating}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ImageCropModal
        file={currentCropFile}
        isOpen={isCropping}
        queueIndex={cropQueueIndex}
        queueTotal={cropBatchTotal}
        onConfirm={handleCropConfirm}
        onUseOriginal={handleCropUseOriginal}
        onUseAllOriginals={handleCropUseAllOriginals}
        onCancelAll={clearCropQueue}
      />
    </div>
  );
};

export default CarsManager;